import { useState, useRef } from 'react';
import { X, Link, Loader2, AlertCircle } from 'lucide-react';
import { mockUploadRecipeByUrl, validateRecipeUrl } from '../utils/recipeImport';
import { apiPost } from '../utils/apiClient';
import { toMinutes } from '../utils/recipeTime';

// useMockData defaults OFF: when on, it returns made-up demo recipes (that don't exist in the database).
export function UrlRecipeForm({ isOpen, onClose, onImportSuccess, userId, useMockData = false }) {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const summaryRef = useRef(null);

  const focusSummary = () => {
    requestAnimationFrame(() => {
      summaryRef.current?.focus();
    });
  };

  const clearFieldError = () => {
    setFieldErrors((prev) => {
      if (!prev.url) return prev;
      const next = { ...prev };
      delete next.url;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Trim whitespace
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      const nextErrors = { url: 'Recipe URL is required.' };
      setFieldErrors(nextErrors);
      focusSummary();
      return;
    }

    // Validate URL format
    const validation = validateRecipeUrl(trimmedUrl);
    if (!validation.valid) {
      const message = validation.error || 'Invalid URL';
      setFieldErrors({ url: message });
      setError(message);
      focusSummary();
      return;
    }

    setIsLoading(true);

    try {
      if (useMockData) {
        // Legacy demo mode using front-end mock data
        const result = await mockUploadRecipeByUrl(trimmedUrl, userId);
        if (result.success && result.recipe) {
          onImportSuccess(result.recipe);
          handleClose();
        } else {
          setError(result.error || 'Failed to import recipe');
        }
      } else {
        // Real backend import using /api/import/url (does NOT save to DB)
        const data = await apiPost('/import/url', { url: trimmedUrl });
        const imported = data.recipe || data;

        if (!imported) {
          setError('Import did not return any recipe data');
          return;
        }

        const parseIngredientString = (line) => {
          if (!line || typeof line !== 'string') return { quantity: '', name: line || '' };
          const s = line.trim();
          if (!s) return { quantity: '', name: '' };

          // Basic pattern: leading quantity (with optional fraction/range) + optional unit + rest as name
          const qtyRegex = /^\s*((?:\d+\s+\d+\/\d+)|(?:\d+\/\d+)|(?:\d+(?:\.\d+)?)(?:-\d+(?:\.\d+)?)?|[¼½¾⅓⅔⅛⅜⅝⅞])\s*/u;
          let rest = s;
          let qtyPart = '';
          let unitPart = '';

          const qtyMatch = s.match(qtyRegex);
          if (qtyMatch) {
            qtyPart = qtyMatch[1].trim();
            rest = s.slice(qtyMatch[0].length).trim();

            const unitMatch = rest.match(/^([^\s,()]+)\b/);
            if (unitMatch) {
              const candidate = unitMatch[1].trim();
              // simple whitelist of common units; anything else is treated as part of the name
              const unitCandidates = ['cup', 'cups', 'tbsp', 'tbs', 'tablespoon', 'tablespoons', 'tsp', 'teaspoon', 'teaspoons', 'g', 'kg', 'ml', 'l', 'oz', 'lb', 'lbs', 'can', 'cans', 'clove', 'cloves', 'slice', 'slices'];
              if (unitCandidates.includes(candidate.toLowerCase())) {
                unitPart = candidate;
                rest = rest.slice(unitMatch[0].length).trim();
              }
            }
          }

          const quantity = [qtyPart, unitPart].filter(Boolean).join(' ').trim();
          const name = rest || s;
          return { quantity, name };
        };

        const ingredients = Array.isArray(imported.ingredients)
          ? imported.ingredients.map((ing) => {
              if (!ing) return { quantity: '', name: '' };
              if (typeof ing === 'string') return parseIngredientString(ing);
              const qtyPart = [ing.qty, ing.unit].filter(Boolean).join(' ').trim();
              const name = ing.name || '';
              if (!qtyPart && !name && typeof ing.text === 'string') {
                return parseIngredientString(ing.text);
              }
              return {
                quantity: qtyPart,
                name: name || ing.text || '',
              };
            })
          : [{ quantity: '', name: '' }];

        const method = Array.isArray(imported.instructions)
          ? imported.instructions.filter(Boolean)
          : imported.instructions
            ? String(imported.instructions)
                .split(/\r?\n/)
                .map((s) => s.trim())
                .filter(Boolean)
            : [''];

        // JSON-LD recipeYield is frequently an array (["4", "4 - 5 as a side"]) and often
        // carries a range or trailing prose. Stripping non-digits and concatenating what's
        // left turned that example into 445, which ManualRecipeForm's clamp then flattened
        // to its 20 ceiling — so take the FIRST number instead, and unwrap arrays first.
        // First number in the site's recipe yield (e.g. "12", "Serves 4-6" -> 4), or null if there
        // isn't one. Null falls back to 4 below, and the form tells the user to check it.
        const parseServings = (val) => {
          if (Array.isArray(val)) val = val[0];
          if (!val) return null;
          if (typeof val === 'number') return val || null;
          const match = String(val).match(/\d+(?:\.\d+)?/);
          return match ? Number(match[0]) || null : null;
        };
        const foundServings = parseServings(imported.servings);

        const rawNutrition = imported.nutrition && typeof imported.nutrition === 'object'
          ? imported.nutrition
          : {};

        const nutrition =
          rawNutrition &&
          (rawNutrition.calories !== undefined ||
            rawNutrition.protein !== undefined ||
            rawNutrition.carbs !== undefined ||
            rawNutrition.fat !== undefined)
            ? {
                calories: Number(rawNutrition.calories) || 0,
                protein: Number(rawNutrition.protein) || 0,
                carbs: Number(rawNutrition.carbs) || 0,
                fat: Number(rawNutrition.fat) || 0,
              }
            : undefined;

        const manualRecipe = {
          name: imported.name || '',
          servings: foundServings ?? 4,
          // Not saved: tells the recipe form to show "We couldn't find a serving size..." at Servings.
          servingsDefaulted: foundServings == null,
          prepTime: toMinutes(imported.prep ?? imported.prepTime),
          cookTime: toMinutes(imported.cook ?? imported.cookTime),
          totalTime: toMinutes(imported.total ?? imported.totalTime),
          source: imported.source || imported.sourceUrl || trimmedUrl,
          ingredients,
          method: method.length ? method : [''],
          photo: imported.image || '',
          image: imported.image || '',
          tags: Array.isArray(imported.tags) ? imported.tags : [],
          mealType: '',
          nutrition,
        };

        onImportSuccess(manualRecipe);
        handleClose();
      }
    } catch (err) {
      const msg = (err && err.message) ? err.message : '';
      if (/private|local address|internal/i.test(msg)) {
        setError('We can’t import from private or internal addresses. Please paste a public recipe URL.');
      } else if (/too large/i.test(msg)) {
        setError('That page is too large to import. Please try a simpler recipe page.');
      } else if (/html pages/i.test(msg)) {
        setError('Only HTML recipe pages can be imported. Please use a normal recipe URL.');
      } else {
        setError('We couldn’t import that link. Please check the URL and try again.');
      }
      console.error('Recipe import error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setError(null);
    setFieldErrors({});
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-card text-foreground rounded-3xl w-full max-w-md shadow-2xl border border-[var(--border)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="text-xl">Import from URL</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6" noValidate>
          {Object.keys(fieldErrors).length > 0 && (
            <div
              ref={summaryRef}
              tabIndex={-1}
              role="alert"
              aria-live="assertive"
              className="mb-4 rounded-2xl border border-[#B85A5A]/30 bg-[#FEF5F5] px-4 py-3 outline-none focus:ring-2 focus:ring-[#B85A5A]"
            >
              <div className="flex items-start gap-2 text-[#B85A5A]">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#B85A5A]" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium">Please fix the highlighted field below.</p>
                  {fieldErrors.url && <p>{fieldErrors.url}</p>}
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label htmlFor="recipe-url" className="block text-sm text-muted-foreground mb-2">
              Recipe URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                clearFieldError();
              }}
              placeholder="https://www.example.com/recipe"
              disabled={isLoading}
              aria-invalid={!!fieldErrors.url}
              aria-describedby={fieldErrors.url ? 'recipe-url-error' : undefined}
              className={`w-full px-4 py-3 bg-input rounded-xl border-0 focus:ring-2 focus:ring-primary outline-none disabled:opacity-60 disabled:cursor-not-allowed text-foreground placeholder-muted-foreground ${fieldErrors.url ? 'ring-2 ring-[#B85A5A]' : ''}`}
            />
            {fieldErrors.url && (
              <div id="recipe-url-error" className="mt-2 flex items-center gap-1.5 text-sm text-[#B85A5A]">
                <AlertCircle className="h-4 w-4 text-[#B85A5A]" />
                <span>{fieldErrors.url}</span>
              </div>
            )}
            
            <p className="text-xs text-muted-foreground mt-2">
              {useMockData 
                ? 'Try URLs with keywords like: pasta, cauliflower, chicken, cookies, quinoa'
                : 'Paste or type a URL from a recipe website to automatically import the recipe details'
              }
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {/* Supported Sites Info */}
          {!useMockData && (
            <div className="mb-6 p-4 bg-muted rounded-xl">
              <h4 className="text-sm mb-2">Supported Recipe Websites</h4>
              <p className="text-xs text-muted-foreground">
                Most popular recipe websites are supported including AllRecipes, Food Network, 
                Bon Appétit, and any site that uses standard recipe markup.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 py-3 px-6 bg-card border border-[var(--border)] text-muted-foreground rounded-full hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 py-3 px-6 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Link className="w-5 h-5" />
                  <span>Import Recipe</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
